import { EmployeeLiteModel } from 'api-hooks/employee';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import employeeColumn from 'components/elements/table/editable-columns/employee-column';
import gradeOptions from 'components/elements/table/editable-columns/grade-options';
import jobLevelOptions from 'components/elements/table/editable-columns/job-level-options';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import { CardContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { BonusPeriodFormType, EmployeeFormType } from './form-type';
import { onAddNewEmployee } from '../hooks/use-get-default-values';

const name = 'employees' as const;

function useGetEmployeeTableList(): IColumn<EmployeeFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const columns = React.useMemo(() => {
    return [
      {
        header: t('common:name'),
        accessorKey: 'employeeId',
        dataType: ColumnDataType.select,
        selectProps: employeeColumn,
        minSize: 250,
      },
      {
        accessorKey: 'role',
        header: t('common:roles'),
        dataType: ColumnDataType.null,
        cell: ({ getValue }) => <>{getValue() as string}</>,
        disabled: true,
      },
      ...(editable
        ? [
            {
              accessorKey: 'previousJobLevel',
              header: t('common:previous_extra', {
                extra: t('common:job_level'),
              }),
              dataType: ColumnDataType.null,
              cell({ row }) {
                const previousJobLevel = row.original.previousJobLevel;
                return previousJobLevel ? t(`enum:${previousJobLevel}`) : '';
              },
              disabled: true,
            },
          ]
        : []),
      {
        header: t('common:job_level'),
        accessorKey: 'jobLevel',
        dataType: ColumnDataType.select,
        options: jobLevelOptions(t),
      },
      ...(editable
        ? [
            {
              accessorKey: 'previousGrade',
              header: t('common:previous_extra', { extra: t('common:grade') }),
              dataType: ColumnDataType.null,
              cell: ({ getValue }) => <>{getValue() as string}</>,
              disabled: true,
            },
          ]
        : []),
      {
        header: t('common:grade'),
        accessorKey: 'grade',
        dataType: ColumnDataType.select,
        options: gradeOptions,
        maxSize: 100,
      },
      ...deleteColumn(editable),
    ];
  }, [editable, t]);
  return columns;
}

export default function EmployeeTableInput() {
  const { editable } = useFormState();
  const { t } = useTranslation();
  const { getValues, clearErrors, control } =
    useFormContext<BonusPeriodFormType>();

  const { fields, append, remove, update } = useFieldArray({
    name,
    keyName: '__customId',
    control,
  });

  const columns = useGetEmployeeTableList();

  const dispatch = React.useCallback(
    (action) => {
      const employees = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const employee = employees[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof EmployeeFormType;
      const value = action.value;

      const otherVal: () => Partial<EmployeeFormType> = () => {
        if (columnId === 'employeeId') {
          const extra = action.extra?.[0]?.extra as EmployeeLiteModel;
          const role = extra.roles.map((role) => role.name).join(', ');
          return {
            role,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(onAddNewEmployee());
      } else if (type === 'update_cell') {
        const _employee = {
          ...employee,
          [columnId]: value,
          ...otherVal(),
        };
        update(currentRowIndex, _employee);
        clearErrors();
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
      }
    },
    [append, clearErrors, getValues, remove, update],
  );

  return (
    <CardContainer>
      <Text variant="LabelLarge">{t('common:employee')}</Text>
      <Separator gap={16} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </CardContainer>
  );
}
